import React, { useRef, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { PaymentMethod } from '../../types/index';
import { Eye, Trash2, MapPin, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import { traduzirMetodoPagamento, pagamentosDoMovimento, formatarResumoPagamentoMisto } from '../../shared/logica/pagamentos';

/**
 * Tabela de movimentos
 */
export default function TabelaMovimentos({ 
  movimentos, 
  onViewMovement,
  onEditMovement,
  onDeleteMovement, 
  canEdit = true,
  canDelete = false
}) {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const [thumbWidth, setThumbWidth] = useState(100);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      
      // Calcular valores da barra customizada
      const maxScroll = scrollWidth - clientWidth;
      const canScroll = scrollWidth > clientWidth;
      
      if (canScroll && maxScroll > 0) {
        const thumbWidthPercent = Math.max(10, (clientWidth / scrollWidth) * 100);
        const scrollPercent = (scrollLeft / maxScroll) * 100;
        setThumbWidth(thumbWidthPercent);
        setScrollPercentage(scrollPercent);
      } else {
        setThumbWidth(100);
        setScrollPercentage(0);
      }
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      const timer = setTimeout(() => {
        checkScrollButtons();
      }, 100);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
        clearTimeout(timer);
      };
    }
  }, [movimentos]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Função para iniciar arrasto (rato ou touch)
  const handleDragStart = (startX, trackElement) => {
    const container = scrollContainerRef.current;
    if (!container || !trackElement) return;
    
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    const maxScroll = scrollWidth - clientWidth;
    const canScroll = scrollWidth > clientWidth;
    
    if (!canScroll || maxScroll <= 0) return;
    
    const thumbWidth = Math.max(10, (clientWidth / scrollWidth) * 100);
    const maxThumbLeft = 100 - thumbWidth;
    const currentScrollLeft = container.scrollLeft;
    const startLeft = maxScroll > 0 && maxThumbLeft > 0 
      ? (currentScrollLeft / maxScroll) * maxThumbLeft 
      : 0;
    
    const handleMove = (currentX) => {
      if (!container || !container.parentElement || !trackElement) {
        return false;
      }
      
      try {
        const rect = trackElement.getBoundingClientRect();
        if (!rect || rect.width === 0) return false;
        
        const trackPixelWidth = rect.width;
        const deltaX = currentX - startX;
        const deltaPercentage = (deltaX / trackPixelWidth) * 100;
        const newThumbLeft = Math.max(0, Math.min(maxThumbLeft, startLeft + deltaPercentage));
        const scrollPercentage = maxThumbLeft > 0 ? newThumbLeft / maxThumbLeft : 0;
        
        if (container && typeof container.scrollLeft !== 'undefined') {
          container.scrollLeft = scrollPercentage * maxScroll;
        }
        return true;
      } catch (error) {
        console.error('Error in handleMove:', error);
        return false;
      }
    };
    
    const handleMouseMove = (e) => {
      if (!handleMove(e.clientX)) {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }
    };
    
    const handleTouchMove = (e) => {
      e.preventDefault();
      if (e.touches && e.touches.length > 0) {
        if (!handleMove(e.touches[0].clientX)) {
          document.removeEventListener('touchmove', handleTouchMove);
          document.removeEventListener('touchend', handleTouchEnd);
        }
      }
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    const handleTouchEnd = () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };

  return (
    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-xl overflow-hidden relative" style={{ paddingBottom: 0 }}>
      <div className="relative" style={{ marginBottom: 0, paddingBottom: 0 }}>
        {/* Botão seta esquerda - apenas PC */}
        <button
          onClick={scrollLeft}
          disabled={!canScrollLeft}
          className="hidden lg:flex absolute left-0 top-0 bottom-0 w-12 items-center justify-center bg-gradient-to-r from-white to-transparent z-10 hover:from-slate-50 transition-all disabled:opacity-0 disabled:pointer-events-none"
          aria-label="Scroll esquerda"
        >
          <ChevronLeft size={20} className="text-slate-600" />
        </button>

        {/* Botão seta direita - apenas PC */}
        <button
          onClick={scrollRight}
          disabled={!canScrollRight}
          className="hidden lg:flex absolute right-0 top-0 bottom-0 w-12 items-center justify-center bg-gradient-to-l from-white to-transparent z-10 hover:from-slate-50 transition-all disabled:opacity-0 disabled:pointer-events-none"
          aria-label="Scroll direita"
        >
          <ChevronRight size={20} className="text-slate-600" />
        </button>

        <div 
          ref={scrollContainerRef}
          className="overflow-x-scroll overflow-y-auto hide-native-scrollbar"
          onScroll={checkScrollButtons}
          onTouchStart={(e) => {
            if (e.touches && e.touches.length > 0) {
              const touch = e.touches[0];
              scrollContainerRef.current?.setAttribute('data-touch-start-x', touch.clientX.toString());
              scrollContainerRef.current?.setAttribute('data-touch-start-scroll', scrollContainerRef.current.scrollLeft.toString());
            }
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            const container = scrollContainerRef.current;
            if (!container || !e.touches || e.touches.length === 0) return;
            
            const touchStartX = parseFloat(container.getAttribute('data-touch-start-x') || '0');
            const touchStartScroll = parseFloat(container.getAttribute('data-touch-start-scroll') || '0');
            const currentX = e.touches[0].clientX;
            const deltaX = touchStartX - currentX;
            
            const maxScroll = container.scrollWidth - container.clientWidth;
            if (maxScroll > 0) {
              container.scrollLeft = Math.max(0, Math.min(maxScroll, touchStartScroll + deltaX));
            }
          }}
          style={{ 
            maxHeight: 'calc(100vh - 200px)',
            touchAction: 'pan-y pinch-zoom'
          }}
        >
        <table
          className="inline-table w-max text-left"
          style={{
            minWidth: 'max-content',
            borderCollapse: 'collapse',
            borderSpacing: 0,
            tableLayout: 'auto',
          }}
        >
          <colgroup>
            <col style={{ width: '1%' }} />
            <col />
            <col style={{ width: '1%' }} />
            <col style={{ width: '1%' }} />
            <col style={{ width: '1%' }} />
            <col style={{ width: '1%' }} />
          </colgroup>
          <thead className="bg-[#1b5e20] text-white/70 text-[10px] font-black uppercase tracking-widest">
            <tr>
              <th className="p-6 whitespace-nowrap">Data</th>
              <th className="p-6 whitespace-nowrap">Cliente / Detalhes</th>
              <th className="p-6 whitespace-nowrap">Pagamento</th>
              <th className="p-6 whitespace-nowrap">Faturação</th>
              <th className="p-6 text-right whitespace-nowrap">Valor</th>
              <th className="p-6 text-center whitespace-nowrap">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {movimentos.map(m => (
              <tr key={m.id} className="group hover:bg-green-50/40 transition-all duration-300">
                <td className="p-6 whitespace-nowrap align-top">
                  <p className="text-sm font-black text-slate-900">{format(new Date(m.date), 'dd/MM/yyyy')}</p>
                  <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-tighter">{m.type}</p>
                </td>
                <td className="p-6 max-w-[520px] whitespace-normal break-words align-top">
                  <p className="font-black text-slate-900 text-lg leading-none break-words">{m.client}</p>
                  <div className="flex flex-col gap-1 mt-2">
                    {m.products && m.products.length > 0 ? (
                      m.products.map((prod, idx) => (
                        <div key={idx} className="flex items-center gap-2 flex-wrap">
                          <span className="inline-block max-w-[420px] text-[10px] text-slate-400 font-bold bg-slate-50 px-2 py-0.5 rounded whitespace-normal break-words">
                            {prod.name}
                          </span>
                          {prod.ref && (
                            <span className="text-[9px] text-slate-500 font-bold bg-slate-100 px-1.5 py-0.5 rounded">
                              Ref: {prod.ref}
                            </span>
                          )}
                        </div>
                      ))
                    ) : (
                      <span className="text-[10px] text-slate-400 font-bold bg-slate-50 px-2 py-0.5 rounded truncate max-w-[150px]">
                        {m.product}
                      </span>
                    )}
                    {(m.deliveryStatus || m.hasDelivery) && (
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded flex items-center gap-1 uppercase w-fit mt-1 ${
                        m.deliveryStatus === 'DELIVERED'
                          ? 'bg-green-50 text-green-600'
                          : 'bg-blue-50 text-blue-600'
                      }`}>
                        <MapPin size={10} /> 
                        {m.deliveryStatus === 'DELIVERED' ? 'Entregue' : 
                         m.deliveryStatus === 'PENDING' || m.hasDelivery ? 'Para Entregar' : 
                         'Entrega'}
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-6 whitespace-nowrap align-top">
                  {m.isMixedPayment ? (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black uppercase text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded w-fit">
                        Misto ({pagamentosDoMovimento(m).length})
                      </span>
                      <div className="text-[9px] text-slate-500 font-bold">
                        {formatarResumoPagamentoMisto(m)}
                      </div>
                      {pagamentosDoMovimento(m).map((p, idx) => (
                        p.method === PaymentMethod.CARD && p.paymentTime ? (
                          <div key={idx} className="text-[9px] text-slate-400 font-black uppercase tracking-widest">
                            Hora {idx + 1}: {p.paymentTime}
                          </div>
                        ) : null
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      <span className={`text-[10px] font-black px-3 py-1.5 rounded-full uppercase border-2 w-fit ${
                        m.paymentMethod === PaymentMethod.CASH ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                        m.paymentMethod === PaymentMethod.CARD ? 'bg-blue-100 text-blue-800 border-blue-200' :
                        m.paymentMethod === PaymentMethod.PENDING ? 'bg-red-50 text-red-700 border-red-200' :
                        m.paymentMethod === PaymentMethod.VOUCHER ? 'bg-purple-100 text-purple-800 border-purple-200' :
                        m.paymentMethod === PaymentMethod.TRANSFER ? 'bg-indigo-100 text-indigo-800 border-indigo-200' :
                        m.paymentMethod === PaymentMethod.CHEQUE ? 'bg-orange-100 text-orange-800 border-orange-200' :
                        m.paymentMethod === PaymentMethod.MBWAY ? 'bg-cyan-100 text-cyan-800 border-cyan-200' :
                        'bg-slate-100 text-slate-600 border-slate-200'
                      }`}>
                        {traduzirMetodoPagamento(m.paymentMethod)}
                      </span>
                      {(m.paymentMethod === PaymentMethod.CARD && m.paymentTime1) && (
                        <div className="text-[9px] text-slate-400 font-black uppercase tracking-widest">
                          Hora: {m.paymentTime1}
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="p-6 whitespace-nowrap align-top">
                  <div>
                    <span className={`text-[9px] font-black px-2 py-1 rounded uppercase tracking-widest ${
                      m.invoiceStatus === 'FATURADO' ? 'bg-green-100 text-green-700' :
                      m.invoiceStatus === 'PARA_FATURAR' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-400'
                    }`}>
                      {m.invoiceStatus?.replace?.('_', ' ') ?? m.invoiceStatus ?? ''}
                    </span>
                    {(() => {
                      const status = String(m.invoiceStatus || '').trim().toUpperCase();
                      const isFaturado = status === 'FATURADO';
                      const num = m.invoiceNumber?.trim?.() || m.invoiceNumber;
                      if (!isFaturado || !num) return null;
                      return (
                        <p className="text-[10px] font-black text-slate-400 mt-1 uppercase leading-none italic">
                          Nº {num}
                        </p>
                      );
                    })()}
                  </div>
                </td>
                <td className="p-6 text-right whitespace-nowrap align-top">
                  <p className="text-2xl font-black text-slate-900 leading-none">{m.value.toFixed(2)}€</p>
                </td>
                <td className="p-6 whitespace-nowrap align-top">
                  <div className="flex items-center justify-center gap-2">
                    <button 
                      type="button"
                      onClick={() => onViewMovement(m)} 
                      className="p-3 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 shadow-sm transition-all" 
                      title="Ver detalhes"
                    >
                      <Eye size={18} />
                    </button>
                    <button 
                      type="button"
                      onClick={() => onEditMovement?.(m)} 
                      className="p-3 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100" 
                      title="Editar"
                    >
                      <Edit size={18} />
                    </button>
                    {canDelete && (
                      <button 
                        type="button"
                        onClick={() => onDeleteMovement(m.id)} 
                        className="p-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all" 
                        title="Eliminar"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {movimentos.length === 0 && (
          <div className="p-20 text-center text-slate-300 italic font-black text-lg">
            Sem resultados.
          </div>
        )}
        </div>
        
        {/* Barra de scroll horizontal customizada - sempre visível apenas em PC */}
        <div className="hidden lg:block h-3 bg-slate-50 border-t border-slate-200 sticky bottom-0 z-20" style={{ marginTop: 0 }}>
          <div className="relative h-full w-full">
            <div 
              className="absolute top-0 h-full bg-slate-300 rounded-full cursor-grab active:cursor-grabbing hover:bg-slate-400 transition-colors"
              style={{
                width: `${thumbWidth}%`,
                left: `${(scrollPercentage / 100) * (100 - thumbWidth)}%`,
                touchAction: 'none'
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleDragStart(e.clientX, e.currentTarget.parentElement);
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (e.touches && e.touches.length > 0) {
                  handleDragStart(e.touches[0].clientX, e.currentTarget.parentElement);
                }
              }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}

